import { Cancel, Check, DirectionsRun, Edit, PlayArrow, PlayDisabled } from '@mui/icons-material';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip } from '~/components/Chip';
import { ActionButton, ActionButtonLink } from '~/components/button';
import { AuthorizedLink } from '~/components/links';
import { useAppContext } from '~/contexts';
import {
  actionTargetName,
  formattedEventName,
  getSignalScheduleDescription,
  humanizeAction,
} from '../../lib';
import type { NetworkDeviceSignals__DeviceSignal as DeviceSignal } from '../../queries/list.generated';
import { Menu } from '../components';

export const useColumns = (
  onCancelClick: (signal: DeviceSignal) => void,
  onRunClick: (signal: DeviceSignal) => void,
): GridColDef<DeviceSignal>[] => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(
    () => [
      {
        field: 'name',
        flex: 0.5,
        headerName: t('common.labels.name'),
        minWidth: 150,
      },
      {
        field: 'kind',
        headerName: t('common.labels.type'),
        maxWidth: 175,
        resizable: false,
        renderCell: ({ row }) =>
          t(`pages.device_signals.settings.fields.kind.${row.kind.toLowerCase()}`),
      },
      {
        field: 'eventType',
        headerName: t('pages.device_signals.list.columns.event_type'),
        minWidth: 150,
        sortable: false,
        valueGetter: (_, row) => row.eventType?.name,
      },
      {
        field: 'event',
        headerName: t('pages.device_signals.list.columns.event'),
        minWidth: 150,
        resizable: false,
        sortable: false,
        valueGetter: (_, row) =>
          formattedEventName(row.event, row.eventType?.signalType || 'COMMON'),
      },
      {
        field: 'action',
        headerName: t('common.labels.action'),
        sortable: false,
        renderCell: ({ row }) => humanizeAction(row.action),
        minWidth: 150,
      },
      {
        field: 'actionTarget',
        headerName: t('common.labels.action_target'),
        sortable: false,
        renderCell: ({ row }) => actionTargetName(row.action),
        minWidth: 150,
      },
      {
        field: 'target',
        headerName: t('pages.device_signals.list.columns.device_target'),
        sortable: false,
        renderCell: ({ row }) =>
          row.allDevices ? (
            'All Devices'
          ) : row.deviceGroup ? (
            <AuthorizedLink authorized={currentNetwork.canManage.value} entity={row.deviceGroup} />
          ) : (
            row.deviceDynamicCollection?.name
          ),
        minWidth: 150,
      },
      {
        field: 'schedule',
        headerName: t('pages.device_signals.list.columns.schedule'),
        sortable: false,
        minWidth: 200,
        flex: 0.5,
        valueGetter: (_, row) =>
          row.schedule ? getSignalScheduleDescription(row.schedule, t) : '',
      },
      {
        field: 'devices',
        headerName: t('common.labels.devices'),
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: ({ row }) => {
          return <Chip color="primary" label={row.deviceCount || 0} />;
        },
        maxWidth: 100,
        resizable: false,
      },
      {
        field: 'status',
        headerName: t('common.labels.status'),
        flex: 0.25,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                color={row.enabled ? 'success' : 'warning'}
                icon={row.enabled ? <Check /> : <PlayDisabled />}
                label={row.enabled ? t('common.status.enabled') : t('common.status.disabled')}
                labelBreakpoint="xl"
              />
              {row.running && (
                <Chip
                  color="tertiary"
                  icon={<DirectionsRun />}
                  label={t('pages.device_signals.list.columns.running')}
                  labelBreakpoint="xl"
                />
              )}
            </Box>
          );
        },
        minWidth: 90,
        resizable: false,
      },
      {
        align: 'right',
        field: 'actions',
        type: 'actions',
        minWidth: isSmallAndUp ? 185 : 0,
        getActions: ({ row }) => {
          const actions = [];

          if (row.canCancel.value) {
            actions.push(
              <ActionButton
                color="error"
                key={`signal-${row.id}-cancel-action`}
                title={t('pages.device_signals.list.actions.cancel_tooltip')}
                Icon={Cancel}
                onClick={() => onCancelClick(row)}
              />,
            );
          }

          actions.push(
            <ActionButton
              disabled={!row.canRun.value}
              key={`signal-${row.id}-run-action`}
              title={
                row.canRun.value
                  ? t('pages.device_signals.list.actions.run_tooltip')
                  : row.canRun.reasons?.fullMessages.join(', ') ??
                    t('pages.device_signals.list.actions.run_disabled_tooltip')
              }
              Icon={PlayArrow}
              onClick={() => onRunClick(row)}
            />,
          );

          actions.push(
            <ActionButtonLink
              key={`edit-${row.id}`}
              title={t('common.buttons.edit')}
              Icon={Edit}
              to={`${row.id}`}
            />,
            <Menu key={`more-action-${row.id}`} deviceSignal={row} />,
          );

          return actions;
        },

        resizable: false,
      },
    ],
    [currentNetwork.canManage.value, isSmallAndUp, onCancelClick, onRunClick, t],
  );
};
